import { create } from "zustand";
import axiosInstance from "../api/axiosInstance";
import { useUserStore } from "./useUserStore";
import useAuthStore from "./useAuthStore"; // Fixed import
import Base64e from '../components/encodedecode/encode';
import { preloadImages } from "../utils/preloadImages";

interface MatchState {
  rows: any[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  showAd: boolean;
  nodata: string;
  filter_status: string;
  location_filter_status: string;
  getMatch: (val: number, retryCount?: number) => Promise<void>;
  strikeout: (id: any) => Promise<void>;
  moreLikeThem: any[];
  getMoreLikeThem: (currentProfileId: any) => Promise<void>;
  cachedMatches: any[];
  blockUser: (blockedUserId: any, requestId: any) => Promise<void>;
  reportUser: (reportedUserId: any, reason: string) => Promise<void>;
}

export const useMatchStore = create<MatchState>((set, get) => ({
  rows: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  showAd: false,
  nodata: "",
  filter_status: "",
  location_filter_status: "",
  cachedMatches: [],
  moreLikeThem: [],
  // Optimized getMatch function
  getMatch: async (page, retryCount = 0) => {

    set({ loading: true });

    try {
      const authStore = useAuthStore.getState();
      const userId = authStore.user?.id;

      if (!userId) {
        console.error("User ID not found. Please log in.");
        set({ loading: false, nodata: "User not authenticated." });
        return;
      }

      // Fetch user details from Zustand store
      const userStore = useUserStore.getState();
      let user = authStore.user;

      // Fetch user details only if not already available
      if (!user) {
        console.log("Fetching user details...");
        user = await userStore.getUserDetails(userId);
      }

      set({
        filter_status: user.filter_status,
        location_filter_status: user.location_filter_status,
      });


      // Check if matches are already cached
      const cachedMatches = get().cachedMatches;
      console.log('cachedMatches.length', cachedMatches.length);
      if (page != -1 && cachedMatches.length > 0 && page < Math.ceil(cachedMatches.length / 1)) {
        console.log("Using cached matches.");
        console.log("page.", page);
        set({
          rows: cachedMatches.slice(page * 1, (page + 1) * 1),
          total: cachedMatches.length,
          totalPages: Math.ceil(cachedMatches.length / 1),
          currentPage: page,
          loading: false,
        });
        preloadImages(cachedMatches.map((match) => match.picture || match.pic));
        return;
      }


      if (page == -1) {
        set({ cachedMatches: [] });

        page = 0;
      }

      // Refresh cachedMatches reference
      const currentMatches = get().cachedMatches ?? [];

      // Sanitize user data to prevent backend SQL crashes (500 errors)
      const sanitizedUser = {
        ...user,
        lat: Number(user.lat) || 0,
        lang: Number(user.lang) || 0,
        distance_from: Number(user.distance_from) || 500,
      };

      // Fetch matches from API
      const matchResponse = await axiosInstance.post("getuserMatch", {
        user: sanitizedUser,
        id: Base64e.encode(userId + ''),
        page,
        limit: 20,
        distance: sanitizedUser.distance_from,
        search: "",
        showAd: false,
        filter_status: user.filter_status,
        location_filter_status: user.location_filter_status,
      });

      if (matchResponse.status === 200 && matchResponse.data.rows.length > 0) {
        const newMatches = matchResponse.data.rows;

        // Start preloading images while updating state
        preloadImages(newMatches.map((match: any) => match.picture || match.pic));

        set({
          rows: newMatches.slice(0, 1), // Show only one profile at a time
          cachedMatches: [...currentMatches, ...newMatches], // Add to cache
          total: matchResponse.data.count,
          totalPages: Math.ceil(matchResponse.data.count / 1),
          currentPage: page,
          loading: false,
          nodata: '',

        });

      } else {
        if (retryCount < 3) { // Max 3 retries
          set({
            rows: [],
            total: 0,
            totalPages: 0,
            currentPage: 0,
            loading: false,
            nodata: "No match found. Please complete your profile.",
          });
          get().getMatch(0, retryCount + 1); // Increment retryCount
        } else {
          set({ nodata: "No match found. Please complete your profile." });
        }

        //get().getMatch(0);
      }
    } catch (error) {
      console.error("Error fetching match data:", error);
      set({ loading: false, nodata: "Error fetching match data. Please try again." });
    }
  },
  // Fetch "More Like Them" profiles separately
  getMoreLikeThem: async (currentProfileId) => {
    set({ loading: true });

    try {
      const authStore = useAuthStore.getState();
      const userId = authStore.user?.id;

      if (!userId) {
        console.error("User ID not found. Please log in.");
        set({ loading: false, nodata: "User not authenticated." });
        return;
      }

      const userStore = useUserStore.getState();
      let user = authStore.user;

      if (!user) {
        console.log("Fetching user details...");
        user = await userStore.getUserDetails(userId);
      }

      const moreLikeResponse = await axiosInstance.post("getuserMatch", {
        user,
        id: Base64e.encode(userId + ""),
        page: 0,
        limit: 5, // Fetch up to 5 similar profiles
        distance: 500,
        search: "",
        showAd: false,
        filter_status: user.filter_status,
        location_filter_status: user.location_filter_status,
        currentProfileId, // Exclude the current profile
        uid: userId
      });

      if (moreLikeResponse.status === 200 && moreLikeResponse.data.rows.length > 0) {
        const newMatches = moreLikeResponse.data.rows;
        const moreLikeThem = newMatches.slice(0, 6);
        set({ moreLikeThem: moreLikeThem, loading: false });
      } else {
        set({ moreLikeThem: [], loading: false, nodata: "No similar profiles found." });
      }
    } catch (error) {
      console.error("Error fetching 'More Like Them' data:", error);
      set({ loading: false, nodata: "Error fetching 'More Like Them' data. Please try again." });
    }
  },

  // Clear cache when needed
  clearCache: () => set({ cachedMatches: [] }),
  // ✅ Added strikeout function to Zustand store
  strikeout: async (id) => {
    const authStore = useAuthStore.getState();
    const userId = authStore.user?.id;

    if (!userId) {
      console.error("User ID not found. Please log in.");
      set({ loading: false, nodata: "User not authenticated." });
      return;
    }
    set({ loading: true });
    try {
      const response = await axiosInstance.post("strikeout", {
        strike: id,
        id: Base64e.encode(userId + ''),
        rq_id: '',
      });

      if (response.status === 200) {
        get().getMatch(0); // Refresh matches after strikeout
      }
    } catch (error) {
      console.error("Error in strikeout:", error);
    } finally {
      set({ loading: false });
    }
  },
  // New blockUser function to block a user
  // Updated blockUser function without reqId
  blockUser: async (blockedUserId, requestId = null) => {
    const authStore = useAuthStore.getState();
    const userId = authStore.user?.id;

    if (!userId) {
      console.error("User ID not found. Please log in.");
      return;
    }

    try {
      const data = {
        user_id: Base64e.encode(userId + ''),
        blocked: Base64e.encode(blockedUserId + ''),
        requestId: requestId
      };

      await axiosInstance.post("blockUser", data);

      // Optionally refresh matches after blocking a user
      get().getMatch(-1);

      console.log(`User ${blockedUserId} blocked successfully.`);
    } catch (error) {
      console.error("Error blocking user:", error);
    }
  },

  // ✅ Report User Function
  reportUser: async (reportedUserId, reason) => {
    try {
      const authStore = useAuthStore.getState();
      const userId = authStore.user?.id;

      if (!userId) {
        console.error("User ID not found. Please log in.");
        return;
      }

      const data = {
        reporter_id: Base64e.encode(userId + ''),
        reported_user_id: Base64e.encode(reportedUserId + ''),
        reason,
      };

      await axiosInstance.post("reportUser", data);

      console.log(`User ${reportedUserId} reported successfully for: ${reason}`);
    } catch (error) {
      console.error("Error reporting user:", error);
    }
  },
}));
